import React, {useEffect, useState} from 'react';
import {Link, useLocation, useSearchParams} from 'react-router-dom';
import Layout from '@/components/Layout';
import {type Article, searchArticles} from '@/api/api';
import styles from '@/styles/Search.module.css';

export default function Search() {
    const location = useLocation();
    const pathname = location.pathname;
    const [searchParams] = useSearchParams();
    const searchString = searchParams.get('q') || '';

    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Define breadcrumbs for this page
    const breadcrumbs = [
        {title: '首页', link: '/'},
        {title: '搜索结果', link: pathname + searchString}
    ];

    useEffect(() => {
        const fetchSearchResults = async () => {
            if (!searchString.trim()) {
                setArticles([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await searchArticles(searchString);

                if (response.data.code === 1) {
                    setArticles(response.data.data);
                } else {
                    setError('搜索失败，请稍后再试');
                }
            } catch (error) {
                console.error('Error searching articles:', error);
                setError('搜索时出错，请稍后再试');
            } finally {
                setLoading(false);
            }
        };

        fetchSearchResults();
    }, [searchString]);

    // Format date from ISO string to YYYY-MM-DD
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Get article link based on category
    const getArticleLink = (article: Article) => {
        const categoryMap: { [key: string]: string } = {
            '新闻中心': '/news',
            '研究动态': '/research',
            '政策法规': '/policies',
            '职业技能': '/skills'
        };

        const baseUrl = categoryMap[article.category] || '/news';
        // Use a consistent pattern - always include a default category if none is specified
        const defaultCategory = article.category === '新闻中心' ? 'notices' : '';
        return `${baseUrl}/${article.labelStr || defaultCategory}/${article.id}`;
    };

    return (
        <Layout>
            <div className={styles.searchContainer}>
                <div className={styles.breadcrumbs}>
                    您当前位置：
                    {breadcrumbs.map((item, index) => (
                        <React.Fragment key={index}>
                            <Link to={item.link}>{item.title}</Link>
                            {index < breadcrumbs.length - 1 ? ' >> ' : ''}
                        </React.Fragment>
                    ))}
                </div>
                <div className={styles.clearfix}></div>

                <div className={styles.searchHeader}>
                    <h1 className={styles.searchTitle}>搜索结果: <span
                        className={styles.searchString}>{searchString}</span></h1>
                </div>

                {loading ? (
                    <div className={styles.searchStatus}>加载中...</div>
                ) : error ? (
                    <div className={styles.searchError}>{error}</div>
                ) : articles.length === 0 ? (
                    <div className={styles.searchStatus}>
                        {searchString.trim() ? '未找到匹配的文章' : '请输入搜索关键词'}
                    </div>
                ) : (
                    <ul className={styles.resultsList}>
                        {articles.map((article) => (
                            <li key={article.id} className={styles.resultItem}>
                                <h3 className={styles.resultTitle}>
                                    <Link to={getArticleLink(article)}>
                                        {article.title}
                                    </Link>
                                </h3>
                                <div className={styles.resultMeta}>
                                    <span className={styles.metaItem}>分类: {article.category}</span>
                                    {article.labelStr && (
                                        <span className={styles.metaItem}>标签: {article.labelStr}</span>
                                    )}
                                    <span className={styles.metaItem}>日期: {formatDate(article.createTime)}</span>
                                </div>
                                <div
                                    className={styles.resultExcerpt}
                                    dangerouslySetInnerHTML={{
                                        __html: article.jsonStr.substring(0, 200) + '...'
                                    }}
                                />
                                <div className={styles.readMore}>
                                    <Link to={getArticleLink(article)} className={styles.readMoreLink}>
                                        阅读全文
                                    </Link>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Layout>
    );
} 